// Panel admin: ventas entrantes de MercadoLibre
// Lee de ml_ventas. Muestra KPIs del mes, filtro por status, timeline con drawer.
// Auto-refresh 30s. Botón manual "Procesar pendientes" que dispara ml-procesar-eventos.

import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import AdminLayout from '../../components/AdminLayout'

// Imprime una etiqueta (imagen) directo a la impresora, forzada a UNA hoja de 100×150mm.
// Usa un iframe oculto (no depende de popups). @page margin:0 + body 100×150 con overflow:hidden
// evita la segunda hoja en blanco que tira la XPrinter. object-fit:contain = no deforma.
function imprimirEtiquetaImg(src, titulo = 'Etiqueta') {
  const prev = document.getElementById('et-print-frame')
  if (prev) prev.remove()
  const iframe = document.createElement('iframe')
  iframe.id = 'et-print-frame'
  iframe.setAttribute('aria-hidden', 'true')
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' })
  document.body.appendChild(iframe)
  const doc = iframe.contentWindow.document
  doc.open()
  doc.write(
    '<!doctype html><html><head><meta charset="utf-8"><title>' + titulo + '</title><style>'
    + '@page{size:100mm 150mm;margin:0}'
    + 'html,body{margin:0;padding:0;width:100mm;height:150mm;overflow:hidden}'
    + 'img{width:100mm;height:150mm;display:block;object-fit:contain}'
    + '</style></head><body><img src="' + src + '"></body></html>'
  )
  doc.close()
  const img = doc.querySelector('img')
  const go = () => { try { iframe.contentWindow.focus(); iframe.contentWindow.print() } catch (e) {} }
  if (img && !img.complete) { img.onload = () => setTimeout(go, 80); img.onerror = go }
  else { setTimeout(go, 150) }
}

// Mapea indicador_facturacion (Biller) -> tasa/label de IVA para mostrar en el preview.
// 1=exento · 2=IVA mínima 10% · 3=IVA básica 22% · 5=entrega gratuita. Default 22% por prudencia.
function ivaDeIndicador(ind) {
  const i = Number(ind)
  if (i === 2) return { rate: 10, label: 'IVA 10%' }
  if (i === 3) return { rate: 22, label: 'IVA 22%' }
  if (i === 1) return { rate: 0, label: 'Exento' }
  if (i === 5) return { rate: 0, label: 'Entrega gratuita' }
  return { rate: 22, label: 'IVA 22%' }
}

// Agrupa las lineas del preview por tasa de IVA. montos_brutos=1 => subtotal_linea es CON IVA.
// Devuelve [{rate,label,neto,iva,total}] ordenado de mayor a menor tasa.
function desgloseIva(lineas) {
  const porTasa = {}
  for (const l of (lineas || [])) {
    const { rate, label } = ivaDeIndicador(l.indicador_facturacion)
    const total = l.subtotal_linea != null ? Number(l.subtotal_linea) : Number(l.precio) * Number(l.cantidad || 1)
    const neto = rate > 0 ? total / (1 + rate / 100) : total
    const key = String(l.indicador_facturacion ?? 3)
    if (!porTasa[key]) porTasa[key] = { rate, label, neto: 0, iva: 0, total: 0 }
    porTasa[key].neto += neto
    porTasa[key].iva += (total - neto)
    porTasa[key].total += total
  }
  return Object.values(porTasa).sort((a, b) => b.rate - a.rate)
}

// Lista de posibles repartidores (admins/superadmins/repartidores activos).
async function cargarPosiblesRepartidores() {
  const { data } = await supabase
    .from('profiles')
    .select('id, nombre, role')
    .in('role', ['admin', 'superadmin', 'repartidor'])
    .not('nombre', 'is', null)
    .order('nombre')
  return data || []
}

const STATUS_META = {
  confirmed:      { bg: '#DBEAFE', color: '#1E40AF', label: 'Confirmada', dot: '#3B82F6' },
  payment_required:{ bg: '#FEF3C7', color: '#92400E', label: 'Espera pago', dot: '#F59E0B' },
  paid:           { bg: '#DCFCE7', color: '#166534', label: 'Pagada', dot: '#10B981' },
  shipped:        { bg: '#F3E8FF', color: '#7C3AED', label: 'Despachada', dot: '#7C3AED' },
  delivered:      { bg: '#DCFCE7', color: '#065F46', label: 'Entregada', dot: '#065F46' },
  cancelled:      { bg: '#FEE2E2', color: '#7F1D1D', label: 'Cancelada', dot: '#EF4444' },
  invalid:        { bg: '#F1F5F9', color: '#475569', label: 'Inválida', dot: '#94A3B8' },
}

export default function VentasML() {
  const [ventas, setVentas] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState({ status: 'todos', busqueda: '' })
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [procesando, setProcesando] = useState(false)
  const [msg, setMsg] = useState(null)
  const [drawerVenta, setDrawerVenta] = useState(null)
  const [previewCfe, setPreviewCfe] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [emitiendo, setEmitiendo] = useState(false)
  const [envioManual, setEnvioManual] = useState('')
  const [posiblesReps, setPosiblesReps] = useState([])
  const [asignando, setAsignando] = useState(false)
  const [regaloML, setRegaloML] = useState(null)

  // Al cambiar de venta se limpia el envío cargado a mano.
  useEffect(() => { setEnvioManual('') }, [drawerVenta?.id])

  // Lista de repartidores (una vez al montar).
  useEffect(() => { cargarPosiblesRepartidores().then(setPosiblesReps) }, [])

  // Completar el costo de envio de todas las ventas que aun no lo tienen (trae del shipment de ML).
  // Corre una vez al montar; el batch solo toca las que faltan, asi que despues no hace nada.
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ml-sync-envios-batch`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session?.access_token || ''}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ limit: 80 }),
        })
        const j = await r.json().catch(() => ({}))
        if (r.ok && j.ok && j.actualizadas > 0) cargar()
      } catch { /* noop */ }
    })()
  }, [])

  // Preview del CFE (dry-run) cuando se abre una venta paga sin factura: se ve qué se va a emitir ANTES.
  // Se re-dispara si cargás un envío a mano (envioManual) para verlo reflejado en el total.
  useEffect(() => {
    setPreviewCfe(null)
    const v = drawerVenta
    if (!v || v.cfe_id || v.factura || !['paid', 'shipped', 'delivered'].includes(v.status)) return
    let cancel = false
    ;(async () => {
      setPreviewLoading(true)
      try {
        const { data: { session } } = await supabase.auth.getSession()
        const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/emitir-cfe`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session?.access_token || ''}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ml_venta_id: v.id, dry_run: true, envio: envioManual ? Number(envioManual) : undefined }),
        })
        const j = await r.json().catch(() => ({}))
        if (!cancel && r.ok && j.ok) setPreviewCfe(j)
      } catch { /* noop */ } finally { if (!cancel) setPreviewLoading(false) }
    })()
    return () => { cancel = true }
  }, [drawerVenta, envioManual])

  // El bucket facturas-emitidas es privado (datos fiscales): el PDF se abre con signed URL temporal,
  // NO con URL pública (esa da "Bucket not found"). Mismo patrón que VentasUnificado / ClienteDetalle.
  async function abrirPdfCfe(pdfPath, win) {
    if (!pdfPath) { if (win) win.close(); alert('Sin PDF disponible todavía.'); return }
    const { data, error } = await supabase.storage.from('facturas-emitidas').createSignedUrl(pdfPath, 300)
    if (error || !data?.signedUrl) { if (win) win.close(); alert('No se pudo abrir el PDF: ' + (error?.message || 'sin acceso al archivo')); return }
    if (win) { win.location = data.signedUrl } else { window.open(data.signedUrl, '_blank', 'noopener,noreferrer') }
  }

  async function cargar() {
    const { data } = await supabase
      .from('ml_ventas')
      .select('id, ml_order_id, ml_order_ids, ml_pack_id, ml_item_id, producto_id, cantidad, cliente_nickname, cliente_email, cliente_nombre, monto_bruto, comision_monto, costo_fijo, iva_debito, neto, neto_real, es_flex, envio_gross, envio_costo_vendedor, envio_compensacion, compensacion_envio_gratis, status, shipping_mode, shipping_status, cfe_id, cfe_pdf_url, date_created, date_paid, date_shipped, date_delivered, cliente_direccion, repartidor_id, entrega_id, entrega_lat, entrega_lng, entrega_direccion_manual, factura:facturas_emitidas!ml_ventas_cfe_id_fkey(id, serie, numero, tipo_cfe, estado_dgi, pdf_path), repartidor:profiles!ml_ventas_repartidor_id_fkey(id, nombre)')
      .order('date_created', { ascending: false })
      .limit(200)
    setVentas(data || [])
    setLoading(false)
  }

  // Abre el drawer al toque y trae el JSON crudo on-demand. raw_order_payload ya no viene
  // en la lista (destostar ese JSON gigante por fila hacía lenta la pantalla).
  async function abrirDrawer(v) {
    setDrawerVenta(v)
    if (v && v.raw_order_payload === undefined) {
      const { data } = await supabase.from('ml_ventas').select('raw_order_payload').eq('id', v.id).maybeSingle()
      if (data) setDrawerVenta(prev => (prev && prev.id === v.id ? { ...prev, raw_order_payload: data.raw_order_payload } : prev))
    }
  }

  useEffect(() => {
    cargar()
    if (!autoRefresh) return
    const t = setInterval(cargar, 30000)
    return () => clearInterval(t)
  }, [autoRefresh])

  async function asignarRepartidor(ml_venta_id, repartidor_id, lat, lng, direccion) {
    setAsignando(true)
    try {
      const { data, error } = await supabase.rpc('ml_asignar_repartidor', {
        p_ml_venta_id: ml_venta_id,
        p_repartidor_id: repartidor_id,
        p_lat: lat || null,
        p_lng: lng || null,
        p_direccion: direccion || null,
      })
      if (error) throw error
      setMsg({ ok: true, txt: `Repartidor asignado (entrega ${data?.numero_orden || data?.entrega_id?.slice(0, 8) || '—'} · ${data?.accion || 'ok'})` })
      await cargar()
      if (drawerVenta) {
        const { data: v } = await supabase.from('ml_ventas')
          .select('*, repartidor:profiles!ml_ventas_repartidor_id_fkey(id, nombre)')
          .eq('id', ml_venta_id).maybeSingle()
        if (v) setDrawerVenta(prev => ({ ...prev, ...v }))
      }
    } catch (e) {
      setMsg({ ok: false, txt: `Error al asignar: ${e.message}` })
    } finally {
      setAsignando(false)
    }
  }

  async function procesarPendientes() {
    setProcesando(true); setMsg(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ml-procesar-eventos`
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ trigger: 'manual' }),
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data?.error || 'error')
      setMsg({ ok: data.exitosos > 0 || data.procesados === 0, txt: `${data.procesados || 0} eventos procesados · ${data.exitosos || 0} OK · ${data.errores || 0} error` })
      cargar()
    } catch (e) {
      setMsg({ ok: false, txt: e.message })
    } finally {
      setProcesando(false)
    }
  }

  const filtradas = useMemo(() => {
    return ventas.filter(v => {
      if (filtro.status !== 'todos' && v.status !== filtro.status) return false
      if (filtro.busqueda) {
        const q = filtro.busqueda.toLowerCase()
        if (!(`${v.ml_order_id || ''} ${v.cliente_nickname || ''} ${v.cliente_nombre || ''}`.toLowerCase().includes(q))) return false
      }
      return true
    })
  }, [ventas, filtro])

  // KPIs mes actual
  const stats = useMemo(() => {
    const inicioMes = new Date()
    inicioMes.setDate(1); inicioMes.setHours(0, 0, 0, 0)
    const delMes = ventas.filter(v => v.date_created && new Date(v.date_created) >= inicioMes)
    const facturadas = delMes.filter(v => ['paid', 'shipped', 'delivered'].includes(v.status))
    const brutoMes = facturadas.reduce((s, v) => s + Number(v.monto_bruto || 0), 0)
    const netoMes = facturadas.reduce((s, v) => s + Number(v.neto_real ?? v.neto ?? 0), 0)
    const comisionMes = facturadas.reduce((s, v) => s + Number(v.comision_monto || 0) + Number(v.costo_fijo || 0), 0)
    const flexMes = facturadas.reduce((s, v) => s + Number(v.envio_compensacion || 0), 0)
    const pendientesDespacho = ventas.filter(v => v.status === 'paid' && !v.date_shipped).length
    const pendientesCfe = ventas.filter(v => !v.cfe_id && ['paid', 'shipped', 'delivered'].includes(v.status)).length
    return { total: delMes.length, brutoMes, netoMes, comisionMes, flexMes, pendientesDespacho, pendientesCfe }
  }, [ventas])

  const fmt = (n) => '$ ' + Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })
  // Con centavos: para la factura (CFE) tiene que coincidir peso a peso con lo que cobró ML, sin redondear.
  const fmt2 = (n) => '$ ' + Number(n || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  // Formato compacto para que entre en una sola línea: "07/08 20:16" en vez de "7/8/26, 8:16 p. m."
  const fmtDate = (s) => {
    if (!s) return '—'
    const d = new Date(s)
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const hh = String(d.getHours()).padStart(2, '0')
    const mi = String(d.getMinutes()).padStart(2, '0')
    return `${dd}/${mm} ${hh}:${mi}`
  }

  return (
    <AdminLayout>
      <div className="page">
        <Link to="/admin/canales/mercadolibre" style={{ fontSize: 13, color: 'var(--muted)' }}>← Volver a MercadoLibre</Link>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
          <div>
            <h1 style={{ margin: 0 }}>🛒 Ventas MercadoLibre</h1>
            <p className="lead" style={{ margin: '4px 0 0' }}>
              Todo lo que ML te informó via webhook. Auto-refresh 30s.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
              <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
              Auto 30s
            </label>
            <button
              onClick={procesarPendientes}
              disabled={procesando}
              style={{
                background: '#3B82F6', color: '#fff', border: 'none',
                padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}
            >
              {procesando ? 'Procesando…' : '⚡ Procesar pendientes'}
            </button>
          </div>
        </div>

        {msg && (
          <div style={{
            marginTop: 12, padding: '10px 14px', borderRadius: 8,
            background: msg.ok ? '#DCFCE7' : '#FEE2E2',
            color: msg.ok ? '#166534' : '#7F1D1D',
            fontSize: 13, fontWeight: 600,
          }}>{msg.txt}</div>
        )}

        {/* Etiqueta de agradecimiento para las bolsas de ML (se imprime en térmica 100×150mm) */}
        <div className="card" style={{ marginTop: 16, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', borderLeft: '4px solid #00A8B5' }}>
          <img
            src="/materiales-ml/etiqueta-gracias-termica.png"
            alt="Etiqueta de agradecimiento Limpack"
            style={{ width: 68, height: 102, objectFit: 'contain', border: '1px solid #E2E8F0', borderRadius: 6, background: '#fff' }}
          />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 800, color: '#1A3461', fontSize: 14 }}>🏷️ Etiqueta de agradecimiento</div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 3, maxWidth: 480 }}>
              Va dentro de la bolsa del envío. Blanco y negro, tamaño etiqueta 100×150mm — se imprime en la XPrinter. Lleva QR a la tienda para que el cliente vuelva a comprar.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => imprimirEtiquetaImg('/materiales-ml/etiqueta-gracias-termica.png', 'Etiqueta agradecimiento')}
                style={{ padding: '7px 14px', background: '#00A8B5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              >🖨 Imprimir</button>
              <a
                href="/materiales-ml/etiqueta-gracias-termica-100x150.pdf"
                download
                style={{ padding: '7px 14px', background: '#fff', color: '#1A3461', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}
              >⬇ PDF (100×150)</a>
              <a
                href="/materiales-ml/etiqueta-gracias-termica.png"
                download
                style={{ padding: '7px 14px', background: '#fff', color: '#1A3461', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}
              >⬇ PNG (203dpi B&N)</a>
              <span style={{ fontSize: 11, color: '#94A3B8', alignSelf: 'center' }}>Imprimí a tamaño real, sin escalar.</span>
            </div>
          </div>
        </div>

        {/* Etiqueta FRÁGIL para pegar en las cajas (se imprime en térmica 100×150mm) */}
        <div className="card" style={{ marginTop: 12, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', borderLeft: '4px solid #1A3461' }}>
          <img
            src="/materiales-ml/etiqueta-fragil-termica.png"
            alt="Etiqueta FRÁGIL Limpack"
            style={{ width: 68, height: 102, objectFit: 'contain', border: '1px solid #E2E8F0', borderRadius: 6, background: '#fff' }}
          />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 800, color: '#1A3461', fontSize: 14 }}>📦 Etiqueta FRÁGIL</div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 3, maxWidth: 480 }}>
              Para pegar en las cajas frágiles. Blanco y negro, 100×150mm — se imprime en la XPrinter. Lleva FRÁGIL + flechas "este lado arriba".
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => imprimirEtiquetaImg('/materiales-ml/etiqueta-fragil-termica.png', 'Etiqueta FRAGIL')}
                style={{ padding: '7px 14px', background: '#1A3461', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              >🖨 Imprimir</button>
              <a
                href="/materiales-ml/etiqueta-fragil-termica-100x150.pdf"
                download
                style={{ padding: '7px 14px', background: '#fff', color: '#1A3461', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}
              >⬇ PDF (100×150)</a>
              <a
                href="/materiales-ml/etiqueta-fragil-termica.png"
                download
                style={{ padding: '7px 14px', background: '#fff', color: '#1A3461', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}
              >⬇ PNG (300dpi B&N)</a>
              <span style={{ fontSize: 11, color: '#94A3B8', alignSelf: 'center' }}>Imprimí a tamaño real, sin escalar.</span>
            </div>
          </div>
        </div>

        {/* KPIs mes */}
        <div className="cards-grid" style={{ marginTop: 16 }}>
          <div className="card"><h3>Ventas del mes</h3><div className="stat">{stats.total}</div></div>
          <div className="card"><h3>Bruto</h3><div className="stat">{fmt(stats.brutoMes)}</div><div className="desc">precio venta ML c/IVA</div></div>
          <div className="card" style={{ borderLeft: '4px solid #10B981' }}><h3>Neto Limpack</h3><div className="stat" style={{ color: '#10B981' }}>{fmt(stats.netoMes)}</div><div className="desc">bruto − comisión + crédito envío</div></div>
          <div className="card" style={{ borderLeft: '4px solid #DC2626' }}><h3>Comisión ML</h3><div className="stat" style={{ color: '#DC2626' }}>{fmt(stats.comisionMes)}</div><div className="desc">real (marketplace_fee)</div></div>
          {stats.flexMes > 0 && (
            <div className="card" style={{ borderLeft: '4px solid #0EA5E9' }}><h3>Crédito envío (Flex)</h3><div className="stat" style={{ color: '#0EA5E9' }}>{fmt(stats.flexMes)}</div><div className="desc">ML te devuelve por repartir</div></div>
          )}
          {stats.pendientesDespacho > 0 && (
            <div className="card" style={{ borderLeft: '4px solid #F59E0B' }}><h3>Pendientes despacho</h3><div className="stat" style={{ color: '#F59E0B' }}>{stats.pendientesDespacho}</div></div>
          )}
          {stats.pendientesCfe > 0 && (
            <div className="card" style={{ borderLeft: '4px solid #7C3AED' }}><h3>CFE sin emitir</h3><div className="stat" style={{ color: '#7C3AED' }}>{stats.pendientesCfe}</div></div>
          )}
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
          <input
            type="text"
            value={filtro.busqueda}
            onChange={e => setFiltro({ ...filtro, busqueda: e.target.value })}
            placeholder="Buscar order / nickname / nombre..."
            style={{ padding: '7px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13, minWidth: 240 }}
          />
          <select
            value={filtro.status}
            onChange={e => setFiltro({ ...filtro, status: e.target.value })}
            style={{ padding: '7px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }}
          >
            <option value="todos">Todos los estados</option>
            {Object.entries(STATUS_META).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>

        {/* Lista */}
        {loading ? (
          <div className="card" style={{ marginTop: 16, color: 'var(--muted)' }}>Cargando…</div>
        ) : filtradas.length === 0 ? (
          <div className="card" style={{ marginTop: 16, color: 'var(--muted)', textAlign: 'center', padding: 32 }}>
            <div style={{ fontSize: 40, opacity: 0.4 }}>🛒</div>
            <p style={{ margin: '10px 0 4px' }}>Sin ventas todavía.</p>
            <p style={{ margin: 0, fontSize: 11, color: '#94A3B8' }}>
              Cuando ML procese la primera compra, va a aparecer acá automáticamente.
            </p>
          </div>
        ) : (
          <table className="t-table" style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th style={{ width: 78 }}>Fecha</th>
                <th style={{ width: 100 }}>Order ID</th>
                <th style={{ minWidth: 140 }}>Cliente</th>
                <th style={{ minWidth: 140 }}>Producto</th>
                <th style={{ textAlign: 'right', width: 80 }}>Bruto</th>
                <th style={{ textAlign: 'right', width: 70 }}>Envío</th>
                <th style={{ textAlign: 'right', width: 90 }} title="Bonificación de ML por envío gratis">Comp. envío</th>
                <th style={{ textAlign: 'right', width: 80 }}>Neto</th>
                <th style={{ width: 100 }}>Estado</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map(v => {
                const status = STATUS_META[v.status] || { bg: '#F1F5F9', color: '#475569', label: v.status || '—', dot: '#94A3B8' }
                return (
                  <tr key={v.id} style={{ cursor: 'pointer' }} onClick={() => abrirDrawer(v)}>
                    <td style={{ fontSize: 12, color: '#64748B', whiteSpace: 'nowrap' }}>{fmtDate(v.date_created)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}><code style={{ fontSize: 11, background: '#F8FAFC', padding: '1px 6px', borderRadius: 3 }}>{v.ml_order_id}</code></td>
                    <td style={{ maxWidth: 200 }}>
                      <div style={{ fontWeight: 700, color: '#1A3461', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={v.cliente_nickname}>{v.cliente_nickname || '—'}</div>
                      {v.cliente_nombre && <div style={{ fontSize: 11, color: '#94A3B8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={v.cliente_nombre}>{v.cliente_nombre}</div>}
                    </td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      {v.ml_item_id
                        ? <code style={{ fontSize: 10.5, color: '#475569' }}>{v.ml_item_id}</code>
                        : <span style={{ color: '#94A3B8' }}>—</span>}
                      {v.cantidad > 1 && <span style={{ marginLeft: 6, fontSize: 11, color: '#64748B' }}>×{v.cantidad}</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmt(v.monto_bruto)}</td>
                    <td style={{ textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }} title={Number(v.envio_gross) > 0 ? `A facturar (c/envío): ${fmt(Number(v.monto_bruto) + Number(v.envio_gross))}` : ''}>
                      {Number(v.envio_gross) > 0
                        ? <span style={{ color: '#0EA5E9', fontWeight: 700 }}>{fmt(v.envio_gross)}</span>
                        : <span style={{ color: '#CBD5E1' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }} title="Bonificación de ML por envío gratis (entra a tu neto, no se factura al cliente)">
                      {Number(v.compensacion_envio_gratis) > 0
                        ? <span style={{ color: '#0EA5E9', fontWeight: 700 }}>{fmt(v.compensacion_envio_gratis)}</span>
                        : <span style={{ color: '#CBD5E1' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right', color: '#10B981', fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmt(v.neto_real ?? v.neto)}</td>
                    <td>
                      <span style={{
                        fontSize: 11, padding: '3px 9px', borderRadius: 999,
                        background: status.bg, color: status.color,
                        fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase',
                      }}>{status.label}</span>
                    </td>
                    <td style={{ fontSize: 11, color: '#94A3B8' }}>
                      {v.factura ? (
                        v.factura.pdf_path ? (
                          <button type="button" onClick={(e) => { e.stopPropagation(); abrirPdfCfe(v.factura.pdf_path) }}
                            style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: 999, background: '#DCFCE7', color: '#166534', fontWeight: 800, letterSpacing: '.03em', textDecoration: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                            title={`CFE ${v.factura.tipo_cfe} · ${v.factura.estado_dgi || 'pendiente DGI'}`}>
                            {v.factura.serie}-{v.factura.numero} 📄
                          </button>
                        ) : (
                          <span style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: 999, background: '#DCFCE7', color: '#166534', fontWeight: 800, letterSpacing: '.03em' }}
                            title={`CFE ${v.factura.tipo_cfe} · ${v.factura.estado_dgi || 'pendiente DGI'}`}>
                            {v.factura.serie}-{v.factura.numero}
                          </span>
                        )
                      ) : (!v.cfe_id && ['paid', 'shipped', 'delivered'].includes(v.status) && (
                        <span title="CFE pendiente" style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: 999, background: '#FEF3C7', color: '#92400E', fontWeight: 700 }}>CFE ⏳</span>
                      ))}
                      {v.status === 'paid' && !v.date_shipped && <span title="Pendiente despacho" style={{ marginLeft: 4 }}>🚚</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {/* Drawer detalle */}
        {drawerVenta && (
          <div
            onClick={() => setDrawerVenta(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000,
              display: 'flex', justifyContent: 'flex-end',
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 640, background: '#fff',
                overflowY: 'auto', padding: 22,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#94A3B8' }}>Order · <code>{drawerVenta.ml_order_id}</code></div>
                  <h2 style={{ margin: '4px 0 0', fontSize: 20, color: '#1A3461' }}>
                    {fmt(drawerVenta.monto_bruto)} <span style={{ fontSize: 13, color: '#94A3B8', fontWeight: 500 }}>bruto</span>
                  </h2>
                </div>
                <button onClick={() => setDrawerVenta(null)} style={{ background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94A3B8' }}>×</button>
              </div>

              {/* Cliente */}
              <div style={{ padding: 12, background: '#F8FAFC', borderRadius: 8, marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Cliente</div>
                <div style={{ fontSize: 14, color: '#1A3461', fontWeight: 700, marginTop: 4 }}>{drawerVenta.cliente_nickname || '—'}</div>
                {drawerVenta.cliente_nombre && <div style={{ fontSize: 12, color: '#475569' }}>{drawerVenta.cliente_nombre}</div>}
                {drawerVenta.cliente_email && <div style={{ fontSize: 12, color: '#475569' }}>{drawerVenta.cliente_email}</div>}
              </div>

              {/* Reparto: quién lo lleva + dirección de envío + coordenadas para km */}
              <RepartoML
                venta={drawerVenta}
                posiblesReps={posiblesReps}
                asignando={asignando}
                onAsignar={asignarRepartidor}
              />

              {/* Math */}
              <div style={{ padding: 12, background: '#F8FAFC', borderRadius: 8, marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, marginBottom: 8 }}>Math del canal</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', fontSize: 12 }}>
                  <span>Bruto</span><strong style={{ textAlign: 'right' }}>{fmt(drawerVenta.monto_bruto)}</strong>
                  <span>Comisión ML {drawerVenta.monto_bruto > 0 ? `(${(Number(drawerVenta.comision_monto || 0) / Number(drawerVenta.monto_bruto) * 100).toFixed(1)}%)` : ''}</span><strong style={{ textAlign: 'right', color: '#DC2626' }}>−{fmt(drawerVenta.comision_monto)}</strong>
                  {Number(drawerVenta.envio_costo_vendedor) > 0 && (<><span>Envío (nos cobra ML)</span><strong style={{ textAlign: 'right', color: '#DC2626' }}>−{fmt(drawerVenta.envio_costo_vendedor)}</strong></>)}
                  {Number(drawerVenta.envio_compensacion) > 0 && (<><span>Crédito envío {drawerVenta.es_flex ? '(Flex)' : ''}</span><strong style={{ textAlign: 'right', color: '#0EA5E9' }}>+{fmt(drawerVenta.envio_compensacion)}</strong></>)}
                  <span style={{ borderTop: '1px dashed #CBD5E1', paddingTop: 4 }}>Neto Limpack</span>
                  <strong style={{ textAlign: 'right', color: '#10B981', borderTop: '1px dashed #CBD5E1', paddingTop: 4, fontSize: 14 }}>{fmt(drawerVenta.neto_real ?? drawerVenta.neto)}</strong>
                </div>
                {Number(drawerVenta.iva_debito) > 0 && (
                  <div style={{ fontSize: 10.5, color: '#94A3B8', marginTop: 6 }}>IVA débito s/comisión: {fmt(drawerVenta.iva_debito)} · crédito fiscal, no afecta el neto de caja.</div>
                )}
                {drawerVenta.ml_order_ids && drawerVenta.ml_order_ids.length > 1 && (
                  <div style={{ fontSize: 10.5, color: '#94A3B8', marginTop: 4 }}>Pack de {drawerVenta.ml_order_ids.length} órdenes agrupadas en esta venta.</div>
                )}
              </div>

              {/* Estado */}
              <div style={{ padding: 12, background: '#F8FAFC', borderRadius: 8, marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, marginBottom: 8 }}>Timeline</div>
                <div style={{ fontSize: 12, color: '#475569', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px' }}>
                  <span>Creada</span><strong>{fmtDate(drawerVenta.date_created)}</strong>
                  {drawerVenta.date_paid && (<><span>Pagada</span><strong>{fmtDate(drawerVenta.date_paid)}</strong></>)}
                  {drawerVenta.date_shipped && (<><span>Despachada</span><strong>{fmtDate(drawerVenta.date_shipped)}</strong></>)}
                  {drawerVenta.date_delivered && (<><span>Entregada</span><strong style={{ color: '#10B981' }}>{fmtDate(drawerVenta.date_delivered)}</strong></>)}
                  {drawerVenta.shipping_mode && (<><span>Modo envío</span><strong>{drawerVenta.shipping_mode}</strong></>)}
                </div>
              </div>

              {/* Vista previa de la factura (CFE) antes de emitir */}
              {!drawerVenta.factura && !drawerVenta.cfe_id && ['paid', 'shipped', 'delivered'].includes(drawerVenta.status) && (
                <div style={{ padding: 14, background: '#fff', border: '1px solid #E7EEF3', borderRadius: 8, marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, marginBottom: 10 }}>Así sale la factura (CFE)</div>
                  {previewLoading && <div style={{ fontSize: 12, color: '#94A3B8' }}>Calculando…</div>}
                  {!previewLoading && !previewCfe && <div style={{ fontSize: 12, color: '#b91c1c' }}>No se pudo generar el preview. Reintentá abriendo la venta de nuevo.</div>}
                  {previewCfe && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: '#00A8B5' }}>{previewCfe.tipo_label}</span>
                        <span style={{ fontSize: 11, color: '#64748B' }}>{previewCfe.receptor?.documento ? `${previewCfe.receptor.tipo_documento === 2 ? 'RUT' : 'CI'} ${previewCfe.receptor.documento}` : 'Consumidor final'}</span>
                      </div>
                      {previewCfe.receptor?.razon_social && <div style={{ fontSize: 14, fontWeight: 700, color: '#1A3461', marginBottom: 8 }}>{previewCfe.receptor.razon_social}</div>}
                      <div style={{ borderTop: '1px solid #EEF2F6', paddingTop: 8 }}>
                        {(previewCfe.lineas || []).map((l, i) => {
                          const cant = Number(l.cantidad || 1)
                          const totalLinea = l.subtotal_linea != null ? Number(l.subtotal_linea) : Number(l.precio) * cant
                          const ivaL = ivaDeIndicador(l.indicador_facturacion)
                          return (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, fontSize: 12, color: '#475569', padding: '3px 0' }}>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                {l.concepto}{cant > 1 ? <span style={{ color: '#94A3B8' }}> · {cant} × {fmt2(l.precio)}</span> : ''}
                              </span>
                              <span style={{ fontSize: 10, fontWeight: 700, color: ivaL.rate === 22 ? '#1A3461' : ivaL.rate === 10 ? '#00A8B5' : '#94A3B8', background: '#F1F5F9', borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}>{ivaL.label}</span>
                              <strong style={{ whiteSpace: 'nowrap' }}>{fmt2(totalLinea)}</strong>
                            </div>
                          )
                        })}
                      </div>
                      {(() => {
                        const desg = desgloseIva(previewCfe.lineas)
                        return (
                          <div style={{ borderTop: '1px dashed #CBD5E1', marginTop: 8, paddingTop: 8, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 12 }}>
                            <span style={{ color: '#64748B' }}>Neto s/IVA</span><strong style={{ textAlign: 'right' }}>{fmt2(previewCfe.subtotal)}</strong>
                            {desg.length > 0 ? desg.map((d, i) => (
                              <Fragment key={i}>
                                <span style={{ color: '#64748B' }}>{d.label}{desg.length > 1 && d.rate > 0 ? <span style={{ color: '#94A3B8' }}> · s/ {fmt2(d.neto)}</span> : ''}</span>
                                <strong style={{ textAlign: 'right' }}>{fmt2(d.iva)}</strong>
                              </Fragment>
                            )) : (
                              <><span style={{ color: '#64748B' }}>IVA</span><strong style={{ textAlign: 'right' }}>{fmt2(previewCfe.iva_total)}</strong></>
                            )}
                            {desg.length > 1 && (
                              <><span style={{ color: '#94A3B8', fontSize: 11 }}>IVA total</span><strong style={{ textAlign: 'right', color: '#94A3B8', fontSize: 11 }}>{fmt2(previewCfe.iva_total)}</strong></>
                            )}
                            <span style={{ fontWeight: 800, color: '#1A3461' }}>Total factura</span><strong style={{ textAlign: 'right', fontSize: 17, color: '#1A3461' }}>{fmt2(previewCfe.monto_total)}</strong>
                          </div>
                        )
                      })()}
                      {previewCfe.envio?.monto > 0 && (
                        <div style={{ fontSize: 10.5, color: '#0EA5E9', marginTop: 6 }}>Incluye envío ${fmt2(previewCfe.envio.monto)} al {previewCfe.envio.iva ?? 22}%{previewCfe.envio.origen === 'manual' ? ' (cargado a mano)' : ''}.</div>
                      )}
                      <div style={{ fontSize: 10.5, color: '#94A3B8', marginTop: 8 }}>Coincide peso a peso con el "Monto total bruto" de ML. La comisión de ML y el crédito Flex NO van en la factura — son tu neto interno.</div>
                    </>
                  )}
                </div>
              )}

              {/* Envío: normalmente lo trae solo el emisor desde el shipment de ML (aparece como línea en el preview).
                  Este bloque SOLO aparece si el automático no lo pudo traer — red de seguridad, no uso normal. */}
              {previewCfe?.envio?.sincronizado === false && !drawerVenta.factura && !drawerVenta.cfe_id && (
                <div style={{ padding: 12, background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, marginBottom: 14 }}>
                  <div style={{ fontSize: 11.5, color: '#92400E', fontWeight: 700, marginBottom: 6 }}>
                    No se pudo traer el envío de ML. Cargalo a mano si corresponde (hereda la tasa del producto):
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>$</span>
                    <input type="text" inputMode="decimal" value={envioManual}
                      onChange={e => setEnvioManual(e.target.value.replace(/[^0-9.]/g, ''))}
                      placeholder="169"
                      style={{ width: 110, padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }} />
                    {envioManual && <button onClick={() => setEnvioManual('')} style={{ fontSize: 11, color: '#0369A1', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>quitar</button>}
                  </div>
                </div>
              )}

              {/* Acciones */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {drawerVenta.producto_id && (
                  <Link to={`/admin/stock/${drawerVenta.producto_id}`} style={{
                    padding: '7px 12px', background: '#1A3461', color: '#fff',
                    borderRadius: 6, textDecoration: 'none', fontSize: 12, fontWeight: 700,
                  }}>→ Ficha producto</Link>
                )}
                <button
                  onClick={() => setRegaloML(drawerVenta)}
                  title="Dar un regalo: emite comprobante de entrega gratuita y descuenta stock (no se cobra, no manda mail)"
                  style={{ padding: '7px 12px', background: '#EDE9FE', color: '#5B21B6', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  🎁 Regalo
                </button>
                {drawerVenta.factura ? (
                  drawerVenta.factura.pdf_path ? (
                    <button type="button" onClick={() => abrirPdfCfe(drawerVenta.factura.pdf_path)}
                      style={{ padding: '7px 12px', background: '#DCFCE7', color: '#166534', borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                      📄 {drawerVenta.factura.serie}-{drawerVenta.factura.numero} · Ver PDF
                    </button>
                  ) : (
                    <span style={{ padding: '7px 12px', background: '#DCFCE7', color: '#166534', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                      📄 {drawerVenta.factura.serie}-{drawerVenta.factura.numero}
                    </span>
                  )
                ) : (!drawerVenta.cfe_id && ['paid', 'shipped', 'delivered'].includes(drawerVenta.status) && (
                  <button
                    disabled={!previewCfe || emitiendo}
                    onClick={async () => {
                      if (!previewCfe) return
                      if (!confirm(`Emitir ${previewCfe.tipo_label} por ${fmt2(previewCfe.monto_total)} a ${previewCfe.receptor?.razon_social || 'consumidor final'}?\nSe emite en DGI vía Biller (no se puede deshacer).`)) return
                      // Abrimos la pestaña del PDF ACÁ (dentro del gesto del click) para que el navegador no
                      // bloquee el popup; después del await le seteamos la URL de la factura ya emitida.
                      const pdfWin = window.open('', '_blank')
                      if (pdfWin) { try { pdfWin.document.write('<p style="font-family:sans-serif;padding:24px;color:#334155">Emitiendo y generando la factura…</p>') } catch (_) {} }
                      setEmitiendo(true)
                      try {
                        const { data: { session } } = await supabase.auth.getSession()
                        const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/emitir-cfe`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ ml_venta_id: drawerVenta.id, envio: envioManual ? Number(envioManual) : undefined }),
                        })
                        const j = await r.json().catch(() => ({}))
                        if (r.ok && j.ok) {
                          // Bucket privado: abrir el PDF con signed URL (no URL pública). pdfWin ya quedó
                          // abierta dentro del gesto del click, así que setear su location tras el await no lo bloquea.
                          if (j.pdf_path) { abrirPdfCfe(j.pdf_path, pdfWin) } else if (pdfWin) { pdfWin.close() }
                          // Dejar el drawer abierto mostrando la factura emitida en vez de cerrarlo.
                          setDrawerVenta(dv => dv ? { ...dv, cfe_id: j.biller_cfe_id || true, factura: { serie: j.serie, numero: j.numero, tipo_cfe: j.tipo_cfe_enum, estado_dgi: dv.factura?.estado_dgi || null, pdf_path: j.pdf_path } } : dv)
                          cargar()
                          if (!j.pdf_path) alert(`CFE emitido · ${j.serie}-${j.numero}. El PDF se está generando; abrilo desde el badge 📄 en unos segundos.`)
                        } else {
                          if (pdfWin) pdfWin.close()
                          // Mostrar el detalle real de Biller (antes quedaba oculto tras el titulo generico).
                          const detalle = [j.error || `HTTP ${r.status}`, j.biller_status ? `Biller HTTP ${j.biller_status}` : null, j.biller_error ? ('Biller: ' + String(j.biller_error).slice(0, 600)) : null].filter(Boolean).join('\n')
                          console.error('[emitir-cfe] rechazo', j)
                          alert(`Error emitiendo CFE:\n${detalle}`)
                        }
                      } catch (e) {
                        if (pdfWin) pdfWin.close()
                        alert(`Error emitiendo CFE: ${e?.message || e}`)
                      } finally { setEmitiendo(false) }
                    }}
                    style={{ padding: '7px 12px', background: (previewCfe && !emitiendo) ? '#F59E0B' : '#CBD5E1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: (previewCfe && !emitiendo) ? 'pointer' : 'not-allowed' }}>
                    {emitiendo ? 'Emitiendo…' : (previewCfe ? `📄 Emitir factura · ${fmt2(previewCfe.monto_total)}` : '📄 Emitir CFE')}
                  </button>
                ))}
                {drawerVenta.status === 'paid' && !drawerVenta.date_shipped && (
                  <span style={{ padding: '7px 12px', background: '#DBEAFE', color: '#1E40AF', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                    🚚 Pendiente despacho (Fase 4)
                  </span>
                )}
              </div>

              {/* Raw payload */}
              <details>
                <summary style={{ cursor: 'pointer', fontSize: 11, color: '#94A3B8' }}>Ver JSON crudo de ML</summary>
                <pre style={{ background: '#F8FAFC', padding: 10, borderRadius: 6, fontSize: 10, overflow: 'auto', maxHeight: 300, marginTop: 6 }}>
                  {JSON.stringify(drawerVenta.raw_order_payload, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        )}
        {regaloML && <RegaloMLModal venta={regaloML} onClose={() => setRegaloML(null)} onDone={() => { setRegaloML(null); cargar() }} />}
      </div>
    </AdminLayout>
  )
}

/* Modal de regalo (entrega gratuita) para una venta de ML */
function RegaloMLModal({ venta, onClose, onDone }) {
  const [catalogo, setCatalogo] = useState([])
  const [q, setQ] = useState('')
  const [regalos, setRegalos] = useState([])
  const [motivo, setMotivo] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [res, setRes] = useState(null)

  useEffect(() => {
    supabase.from('productos').select('id, sku, nombre, marca, stock_actual, stock_reservado')
      .eq('activo', true).order('nombre')
      .then(({ data }) => setCatalogo(data || []))
  }, [])

  const disp = (p) => Number(p.stock_actual || 0) - Number(p.stock_reservado || 0)
  const t = q.trim().toLowerCase()
  const filtrados = (t ? catalogo.filter(p => [p.nombre, p.marca, p.sku].filter(Boolean).join(' ').toLowerCase().includes(t)) : catalogo).slice(0, 30)

  function agregar(prod) {
    setRegalos(prev => {
      const idx = prev.findIndex(r => r.producto_id === prod.id)
      if (idx >= 0) { const n = [...prev]; n[idx] = { ...n[idx], cantidad: (n[idx].cantidad || 1) + 1 }; return n }
      return [...prev, { producto_id: prod.id, sku: prod.sku, nombre: prod.nombre, cantidad: 1, stock: disp(prod) }]
    })
    setQ('')
  }
  const quitar = (i) => setRegalos(prev => prev.filter((_, j) => j !== i))
  const setCant = (i, v) => setRegalos(prev => prev.map((r, j) => j === i ? { ...r, cantidad: Math.max(1, Number(v) || 1) } : r))

  async function emitir() {
    if (!regalos.length) { setErr('Agregá al menos un producto'); return }
    setBusy(true); setErr(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/emitir-regalo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session?.access_token || ''}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productos: regalos.map(r => ({ producto_id: r.producto_id, cantidad: r.cantidad })),
          ml_venta_id: venta.id,
          motivo: motivo || `Regalo venta ML ${venta.ml_order_id || venta.id}`,
        }),
      })
      const data = await r.json()
      if (!r.ok || !data.ok) throw new Error(data.biller_error || data.error || 'No se pudo emitir el regalo')
      setRes(data); setBusy(false)
    } catch (e) { setErr(String(e.message || e)); setBusy(false) }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, maxWidth: 560, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(15,23,42,.3)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, color: '#1A3461', fontWeight: 800 }}>🎁 Dar un regalo · ML</h3>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>Comprobante de entrega gratuita ($0) + baja de stock. No manda mail.</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: '#64748B', fontSize: 20 }}>✕</button>
        </div>
        <div style={{ padding: '14px 20px', overflowY: 'auto', flex: 1 }}>
          {!res ? (
            <>
              <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar producto por nombre / SKU…" style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: 13, marginBottom: 8, fontFamily: 'inherit' }} />
              {q && filtrados.length > 0 && (
                <div style={{ border: '1px solid #E2E8F0', borderRadius: 8, maxHeight: 180, overflowY: 'auto', marginBottom: 10 }}>
                  {filtrados.map(p => (
                    <button key={p.id} onClick={() => agregar(p)} style={{ display: 'flex', justifyContent: 'space-between', width: '100%', padding: '8px 12px', border: 0, borderBottom: '1px solid #F1F5F9', background: '#fff', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                      <span style={{ fontSize: 13, color: '#1A3461' }}><b>{p.sku}</b> {p.nombre}</span>
                      <span style={{ fontSize: 11, color: '#94A3B8' }}>disp {disp(p)}</span>
                    </button>
                  ))}
                </div>
              )}
              {regalos.length === 0 ? (
                <div style={{ color: '#94A3B8', fontSize: 12, padding: 8 }}>Buscá y agregá el/los producto(s) de regalo.</div>
              ) : regalos.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 10px', marginBottom: 6 }}>
                  <div style={{ flex: 1 }}><div style={{ fontWeight: 700, fontSize: 13, color: '#1A3461' }}>{r.nombre}</div><div style={{ fontSize: 11, color: '#94A3B8' }}>{r.sku} · disp {r.stock}</div></div>
                  <input type="number" min="1" value={r.cantidad} onChange={e => setCant(i, e.target.value)} style={{ width: 56, padding: '4px 6px', borderRadius: 6, border: '1px solid #CBD5E1', fontSize: 13, textAlign: 'right', fontFamily: 'inherit' }} />
                  <button onClick={() => quitar(i)} style={{ background: '#FEF2F2', color: '#B91C1C', border: 0, borderRadius: 6, padding: '5px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✕</button>
                </div>
              ))}
              <input type="text" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Motivo (opcional)" style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: 12.5, marginTop: 8, fontFamily: 'inherit' }} />
              {err && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 8 }}>{err}</div>}
            </>
          ) : (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '12px 14px' }}>
              <b style={{ color: '#166534' }}>✅ Regalo emitido.</b> Entrega gratuita <b>{res.serie}-{res.numero}</b> ($0). Sin mail.
              {Array.isArray(res.stock_result) && <div style={{ marginTop: 6, fontSize: 12, color: '#475569' }}>{res.stock_result.map((s, i) => <div key={i}>{s.sku}: {s.error ? `⚠ ${s.error}` : `stock ${s.stock_actual}`}</div>)}</div>}
            </div>
          )}
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid #E2E8F0', background: '#F8FAFC', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {!res ? (
            <>
              <button onClick={onClose} disabled={busy} style={{ background: 'transparent', color: '#475569', border: '1.5px solid #E2E8F0', borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={emitir} disabled={busy || !regalos.length} style={{ background: (busy || !regalos.length) ? '#CBD5E1' : '#00A8B5', color: '#fff', border: 0, borderRadius: 8, padding: '7px 16px', fontSize: 12.5, fontWeight: 700, cursor: (busy || !regalos.length) ? 'not-allowed' : 'pointer' }}>{busy ? 'Emitiendo…' : '🎁 Emitir regalo + descontar'}</button>
            </>
          ) : (
            <button onClick={onDone} style={{ background: '#00A8B5', color: '#fff', border: 0, borderRadius: 8, padding: '7px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Listo, cerrar</button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Bloque "Reparto" del drawer ML: quién lo lleva + dirección de envío + lat/lng
// La Dirección se precarga de entrega_direccion_manual o cliente_direccion (dirección de envío real de ML).
// ─────────────────────────────────────────────────────────────────────
function RepartoML({ venta, posiblesReps, asignando, onAsignar }) {
  const [repId, setRepId] = useState(venta.repartidor_id || '')
  const [lat, setLat] = useState(venta.entrega_lat ?? '')
  const [lng, setLng] = useState(venta.entrega_lng ?? '')
  const [direccion, setDireccion] = useState(venta.entrega_direccion_manual || venta.cliente_direccion || '')
  const [traendo, setTraendo] = useState(false)

  // Trae la direccion de ENVIO real del shipment de ML (receiver_address) y precarga el campo.
  async function traerDireccionML() {
    setTraendo(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ml-sync-shipment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session?.access_token || ''}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ml_venta_id: venta.id }),
      })
      const j = await r.json().catch(() => ({}))
      if (r.ok && j.ok) {
        if (j.direccion) setDireccion(j.direccion)
        if (j.lat != null) setLat(j.lat)
        if (j.lng != null) setLng(j.lng)
        if (!j.direccion) alert('ML no devolvió dirección para este envío.')
      } else {
        alert(`No se pudo traer la dirección de ML: ${j.error || r.status}`)
      }
    } catch (e) {
      alert(`Error trayendo dirección: ${e.message}`)
    } finally { setTraendo(false) }
  }

  useEffect(() => {
    setRepId(venta.repartidor_id || '')
    setLat(venta.entrega_lat ?? '')
    setLng(venta.entrega_lng ?? '')
    setDireccion(venta.entrega_direccion_manual || venta.cliente_direccion || '')
  }, [venta.id, venta.cliente_direccion])

  const dirty = repId !== (venta.repartidor_id || '')
             || String(lat) !== String(venta.entrega_lat ?? '')
             || String(lng) !== String(venta.entrega_lng ?? '')
             || direccion !== (venta.entrega_direccion_manual || venta.cliente_direccion || '')

  return (
    <div style={{ padding: 12, background: '#F0F9FF', borderRadius: 8, marginBottom: 14, border: '1px solid #BAE6FD' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: '#0369A1', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 800 }}>🚚 Reparto</div>
        {venta.repartidor?.nombre && (
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: '#E0E7FF', color: '#3730A3', fontWeight: 700 }}>
            actual: {venta.repartidor.nombre}
          </span>
        )}
      </div>

      <button onClick={traerDireccionML} disabled={traendo}
        style={{ marginBottom: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, background: '#fff', color: '#0369A1', border: '1px solid #7DD3FC', borderRadius: 6, cursor: traendo ? 'wait' : 'pointer' }}>
        {traendo ? 'Trayendo…' : '📍 Traer dirección de envío (ML)'}
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 10px', fontSize: 12, alignItems: 'center' }}>
        <label>Quién lo lleva</label>
        <select value={repId} onChange={e => setRepId(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #CBD5E1', fontSize: 12 }}>
          <option value="">— elegir —</option>
          {posiblesReps.map(p => (
            <option key={p.id} value={p.id}>{p.nombre} · {p.role}</option>
          ))}
        </select>

        <label>Dirección</label>
        <input type="text" value={direccion} onChange={e => setDireccion(e.target.value)}
          placeholder="Cavia 2981, Pocitos"
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #CBD5E1', fontSize: 12 }} />

        <label>Latitud</label>
        <input type="number" step="any" value={lat} onChange={e => setLat(e.target.value)}
          placeholder="-34.9089 (opcional, para km)"
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #CBD5E1', fontSize: 12, fontFamily: 'monospace' }} />

        <label>Longitud</label>
        <input type="number" step="any" value={lng} onChange={e => setLng(e.target.value)}
          placeholder="-56.1552"
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #CBD5E1', fontSize: 12, fontFamily: 'monospace' }} />
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
        <span style={{ fontSize: 10.5, color: '#64748B', marginRight: 'auto' }}>
          {venta.entrega_id
            ? '✓ Ya existe entrega. Al asignar/actualizar se recalcula km.'
            : 'Al asignar se crea una entrega con canal=mercadolibre.'}
        </span>
        <button
          onClick={() => onAsignar(venta.id, repId, lat === '' ? null : Number(lat), lng === '' ? null : Number(lng), direccion || null)}
          disabled={!repId || !dirty || asignando}
          style={{
            padding: '7px 14px', borderRadius: 6, border: 'none',
            background: (!repId || !dirty) ? '#CBD5E1' : '#0369A1',
            color: '#fff', fontSize: 12, fontWeight: 800,
            cursor: (!repId || !dirty || asignando) ? 'not-allowed' : 'pointer',
          }}
        >{asignando ? 'Guardando…' : (venta.entrega_id ? '✓ Actualizar' : '✓ Asignar y crear entrega')}</button>
      </div>
    </div>
  )
}
